import { toast } from 'react-toastify';
import axios from 'axios';

// Error types for better error categorization
export enum PolicyErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  CONFLICT_ERROR = 'CONFLICT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// Structured error information
export interface PolicyError {
  type: PolicyErrorType;
  message: string;
  details?: string;
  statusCode?: number;
  retryable: boolean;
}

// User-friendly error messages
const ERROR_MESSAGES: Record<PolicyErrorType, string> = {
  [PolicyErrorType.VALIDATION_ERROR]: 'Please check your input and try again.',
  [PolicyErrorType.NETWORK_ERROR]: 'Unable to connect to the server. Please check your internet connection.',
  [PolicyErrorType.SERVER_ERROR]: 'Server is experiencing issues. Please try again later.',
  [PolicyErrorType.AUTHENTICATION_ERROR]: 'Session expired. Please refresh the page and try again.',
  [PolicyErrorType.AUTHORIZATION_ERROR]: 'You do not have permission to perform this action.',
  [PolicyErrorType.NOT_FOUND_ERROR]: 'The requested resource was not found.',
  [PolicyErrorType.CONFLICT_ERROR]: 'This action conflicts with existing data. Please refresh and try again.',
  [PolicyErrorType.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
};

// HTTP status code to error type mapping
const STATUS_CODE_TO_ERROR_TYPE: Record<number, PolicyErrorType> = {
  400: PolicyErrorType.VALIDATION_ERROR,
  401: PolicyErrorType.AUTHENTICATION_ERROR,
  403: PolicyErrorType.AUTHORIZATION_ERROR,
  404: PolicyErrorType.NOT_FOUND_ERROR,
  409: PolicyErrorType.CONFLICT_ERROR,
  422: PolicyErrorType.VALIDATION_ERROR,
  500: PolicyErrorType.SERVER_ERROR,
  502: PolicyErrorType.SERVER_ERROR,
  503: PolicyErrorType.SERVER_ERROR,
  504: PolicyErrorType.SERVER_ERROR,
};

/**
 * Parses error from API response or axios error
 */
export const parseError = (error: unknown): PolicyError => {
  // Handle axios errors
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status;
    const responseData = error.response?.data;

    // Network error (no response received)
    if (!error.response) {
      return {
        type: PolicyErrorType.NETWORK_ERROR,
        message: ERROR_MESSAGES[PolicyErrorType.NETWORK_ERROR],
        details: error.message,
        retryable: true,
      };
    }

    // Determine error type from status code
    const errorType = statusCode
      ? STATUS_CODE_TO_ERROR_TYPE[statusCode] || PolicyErrorType.UNKNOWN_ERROR
      : PolicyErrorType.UNKNOWN_ERROR;

    // Extract error message from response
    let errorMessage = ERROR_MESSAGES[errorType];
    if (responseData?.message) {
      errorMessage = responseData.message;
    } else if (responseData?.error) {
      errorMessage = responseData.error;
    }

    return {
      type: errorType,
      message: errorMessage,
      details: error.message,
      statusCode,
      retryable: [PolicyErrorType.NETWORK_ERROR, PolicyErrorType.SERVER_ERROR].includes(errorType),
    };
  }

  // Handle validation errors (from yup or other sources)
  if (error instanceof Error && error.name === 'ValidationError') {
    return {
      type: PolicyErrorType.VALIDATION_ERROR,
      message: error.message,
      retryable: false,
    };
  }

  // Handle generic errors
  if (error instanceof Error) {
    return {
      type: PolicyErrorType.UNKNOWN_ERROR,
      message: error.message,
      retryable: false,
    };
  }

  // Handle unknown error types
  return {
    type: PolicyErrorType.UNKNOWN_ERROR,
    message: 'An unexpected error occurred',
    details: String(error),
    retryable: false,
  };
};

/**
 * Shows appropriate toast notification based on error type
 */
export const showErrorToast = (error: PolicyError, operation: string = 'operation') => {
  const toastOptions = {
    position: toast.POSITION.TOP_RIGHT,
    autoClose: error.retryable ? 5000 : 8000,
  };

  const message = `Failed to ${operation}: ${error.message}`;

  switch (error.type) {
    case PolicyErrorType.VALIDATION_ERROR:
      toast.error(message, toastOptions);
      break;
    case PolicyErrorType.NETWORK_ERROR:
      toast.error(`${message} ${error.retryable ? 'Please try again.' : ''}`, toastOptions);
      break;
    case PolicyErrorType.SERVER_ERROR:
      toast.error(`${message} Our team has been notified.`, toastOptions);
      break;
    case PolicyErrorType.AUTHENTICATION_ERROR:
      toast.error(message, { ...toastOptions, autoClose: false });
      break;
    case PolicyErrorType.AUTHORIZATION_ERROR:
      toast.warning(message, toastOptions);
      break;
    default:
      toast.error(message, toastOptions);
  }
};

/**
 * Handles policy operation errors with automatic retry logic
 */
export const handlePolicyError = (
  error: unknown,
  operation: string,
  retryFn?: () => Promise<void>,
  maxRetries: number = 2
): void => {
  const policyError = parseError(error);

  // Log error for debugging
  // eslint-disable-next-line no-console
  console.error(`Policy ${operation} error:`, {
    type: policyError.type,
    message: policyError.message,
    details: policyError.details,
    statusCode: policyError.statusCode,
  });

  // Show error toast
  showErrorToast(policyError, operation);

  // Handle retryable errors
  if (policyError.retryable && retryFn && maxRetries > 0) {
    // Simple exponential backoff
    const delay = Math.min(1000 * Math.pow(2, 2 - maxRetries), 5000);

    setTimeout(async () => {
      try {
        await retryFn();
      } catch (retryError) {
        handlePolicyError(retryError, operation, retryFn, maxRetries - 1);
      }
    }, delay);
  }
};

/**
 * Utility for wrapping API calls with error handling
 */
export const withErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  operation: string,
  retryFn?: () => Promise<void>
): Promise<T | null> => {
  try {
    return await apiCall();
  } catch (error) {
    handlePolicyError(error, operation, retryFn);
    return null;
  }
};

/**
 * Creates a user-friendly error message for display in UI components
 */
export const getDisplayErrorMessage = (error: unknown): string => {
  const policyError = parseError(error);
  return policyError.message;
};

/**
 * Checks if an error is retryable
 */
export const isRetryableError = (error: unknown): boolean => {
  const policyError = parseError(error);
  return policyError.retryable;
};
